import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import Cell from './Cell';
import { getGridArea } from '../constants/topology';
import { cn } from '../utils/cn';

const Block = ({ block, isZoomed, onCellUpdate, onBlockClick, onSwapCells }) => {
  // Drag state managed at Block level for reliable custom DnD
  const [dragSourceId, setDragSourceId] = useState(null);
  const [dragTargetId, setDragTargetId] = useState(null);
  const [dragClonePos, setDragClonePos] = useState(null);
  const [dragCloneContent, setDragCloneContent] = useState('');

  // Refs for stable access inside global event listeners
  const dragRef = useRef(null);
  const onSwapRef = useRef(onSwapCells);
  const blockIdRef = useRef(block.id);
  onSwapRef.current = onSwapCells;
  blockIdRef.current = block.id;

  const handleDragHandleDown = (e, cell) => {
    if (cell.is_center || !isZoomed) return;

    dragRef.current = {
      sourceId: cell.id,
      content: cell.content || '',
      targetId: null,
      active: true,
    };

    setDragSourceId(cell.id);
    setDragCloneContent(cell.content || '');
    setDragClonePos({ x: e.clientX, y: e.clientY });
    setDragTargetId(null);
  };

  // Attach global pointer listeners when a drag is active
  useEffect(() => {
    if (dragSourceId === null) return;

    const handlePointerMove = (e) => {
      const info = dragRef.current;
      if (!info || !info.active) return;

      info.x = e.clientX;
      info.y = e.clientY;
      setDragClonePos({ x: e.clientX, y: e.clientY });

      // Find target cell under cursor
      const elements = document.elementsFromPoint(e.clientX, e.clientY);
      let newTargetId = null;

      for (const el of elements) {
        const cellEl = el.closest?.('[data-cell-id]');
        if (cellEl && cellEl.dataset.isCenter !== 'true') {
          const id = parseInt(cellEl.dataset.cellId, 10);
          if (id !== info.sourceId) {
            newTargetId = id;
            break;
          }
        }
      }

      info.targetId = newTargetId;
      setDragTargetId(newTargetId);
    };

    const handlePointerUp = () => {
      const info = dragRef.current;
      if (info && info.active) {
        const { sourceId, targetId } = info;
        if (targetId !== null && onSwapRef.current) {
          onSwapRef.current(blockIdRef.current, sourceId, targetId);
        }
      }
      dragRef.current = null;
      setDragSourceId(null);
      setDragTargetId(null);
      setDragClonePos(null);
      setDragCloneContent('');
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [dragSourceId !== null]); // eslint-disable-line react-hooks/exhaustive-deps

  const getBlockGridArea = () => {
    if (block.block_type === 'CORE') return getGridArea(true, null);
    const priority = parseInt(block.block_type.replace('BLOCK_', ''), 10);
    return getGridArea(false, priority);
  };

  const style = isZoomed ? {} : { gridArea: getBlockGridArea() };

  const zoomedClass = isZoomed
    ? 'grid grid-cols-3 grid-rows-3 gap-2 md:gap-3 aspect-square w-full h-full'
    : '';

  const overviewClass = !isZoomed
    ? 'grid grid-cols-3 grid-rows-3 gap-0.5 md:gap-1 p-0.5 md:p-1 backdrop-blur-xl bg-white/60 dark:bg-white/5 rounded-xl border-2 shadow-sm'
    : '';

  const borderClass = block.block_type === 'CORE'
    ? 'border-primary-600 dark:border-primary-400 shadow-md ring-2 ring-primary-600/15 dark:ring-primary-400/20'
    : 'border-white/60 dark:border-white/10 hover:border-primary-400/60 dark:hover:border-primary-500/50';

  return (
    <motion.div
      layoutId={`block-${block.id}`}
      style={style}
      className={cn(
        zoomedClass,
        overviewClass,
        borderClass,
        !isZoomed && 'cursor-pointer transition-all hover:scale-[1.02] z-0 hover:z-10'
      )}
      onClick={() => {
        if (!isZoomed && onBlockClick) onBlockClick(block.id);
      }}
    >
      {[...block.cells].sort((a, b) => {
        if (a.is_center) return -1;
        if (b.is_center) return 1;
        return a.priority_index - b.priority_index;
      }).map((cell) => (
        <Cell
          key={cell.id}
          cell={cell}
          blockType={block.block_type}
          isZoomed={isZoomed}
          onChange={(content) => onCellUpdate(block.id, cell.id, content)}
          readOnly={!isZoomed}
          isBeingDragged={dragSourceId === cell.id}
          isDropTarget={dragTargetId === cell.id}
          onDragHandleDown={isZoomed ? handleDragHandleDown : undefined}
        />
      ))}

      {/* Floating drag clone that follows cursor */}
      {dragClonePos && dragSourceId !== null && (
        <div
          className="fixed z-[9999] pointer-events-none rounded-lg border-2 border-primary-500 shadow-2xl bg-white/90 dark:bg-gray-800/90 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white max-w-[120px] text-center backdrop-blur-sm"
          style={{
            left: dragClonePos.x - 60,
            top: dragClonePos.y - 20,
          }}
        >
          {dragCloneContent || '...'}
        </div>
      )}
    </motion.div>
  );
};

export default Block;